import { intFromUnknown, strFromUnknown } from '@/lib/primitive-utils';
import { SquarePen } from 'lucide-react';
import { useState } from 'react';
import { VIRTUAL_KEYBOARD_LAYOUT_OPTIONS } from '../mathlive/mathlive-keyboard';
import { LabeledSelectRow, LabeledTextRow } from './shared/field-primitives';
import { LatexStringArrayEditorModal } from './shared/latex-string-array-editor-modal';
import { ToleranceField, WidthField } from './shared/tolerance-width-fields';
import type { BuilderPropertyRecord, ShortAnswerToolSettingsProps } from './tool-defaults';

export function selectSymbolicEquivalenceToolProps(p: BuilderPropertyRecord) {
    return {
        expected_variables: strFromUnknown(p.expected_variables, ''),
        expected_functions: strFromUnknown(p.expected_functions, ''),
        accepted_substitute_variables: strFromUnknown(p.accepted_substitute_variables, ''),
        tolerance: strFromUnknown(p.tolerance, '0'),
        virtual_keyboard_layout_name: strFromUnknown(p.virtual_keyboard_layout_name, 'Kitchen sink'),
        width: intFromUnknown(p.width, 150),
    };
}

function SymbolicEquivalenceToolSettingsInner(
    props: ReturnType<typeof selectSymbolicEquivalenceToolProps> & {
        onPatch: (patch: BuilderPropertyRecord) => void;
    },
) {
    const {
        expected_variables,
        expected_functions,
        accepted_substitute_variables,
        tolerance,
        virtual_keyboard_layout_name,
        width,
        onPatch,
    } = props;
    const [expectedVariablesEditorOpen, setExpectedVariablesEditorOpen] = useState(false);

    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2">
            <LabeledTextRow
                label="Expected variables"
                description='Ex: ["\\Delta l","E","d_0"]'
                value={expected_variables}
                rows={2}
                monospace
                labelAccessory={
                    <button
                        type="button"
                        className="cursor-pointer rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                        aria-label="Edit expected variables"
                        title="Edit variables with preview"
                        onClick={() => setExpectedVariablesEditorOpen(true)}
                    >
                        <SquarePen className="h-3.5 w-3.5" />
                    </button>
                }
                onValueChange={v => onPatch({ expected_variables: v })}
            />
            <LatexStringArrayEditorModal
                open={expectedVariablesEditorOpen}
                onOpenChange={setExpectedVariablesEditorOpen}
                title="Expected variables"
                description="Add each variable as LaTeX (without delimiters). Order is preserved in the saved JSON array."
                value={expected_variables}
                onApply={v => onPatch({ expected_variables: v })}
            />
            <LabeledTextRow
                label="Expected functions"
                description='Ex: ["f","g"]'
                value={expected_functions}
                rows={2}
                monospace
                onValueChange={v => onPatch({ expected_functions: v })}
            />
            <LabeledTextRow
                label="Derived variables"
                description='Ex: [["A_0","\\pi (\\frac{d_0}{2})^2"]]'
                value={accepted_substitute_variables}
                rows={2}
                monospace
                onValueChange={v => onPatch({ accepted_substitute_variables: v })}
            />
            <ToleranceField
                tolerance={tolerance}
                onPatch={onPatch}
            />
            <LabeledSelectRow
                label="Virtual keyboard layout"
                value={virtual_keyboard_layout_name}
                options={VIRTUAL_KEYBOARD_LAYOUT_OPTIONS}
                onValueChange={v => onPatch({ virtual_keyboard_layout_name: v })}
            />
            <WidthField
                width={width}
                onPatch={onPatch}
            />
        </div>
    );
}

export function SymbolicEquivalenceToolSettings({ builderProps, onPatch }: ShortAnswerToolSettingsProps) {
    return (
        <SymbolicEquivalenceToolSettingsInner
            {...selectSymbolicEquivalenceToolProps(builderProps)}
            onPatch={onPatch}
        />
    );
}
